import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useProfileId } from '@/features/org/hooks/use-profile-id';
import {
  getMyPrivateNote,
  upsertMyPrivateNote,
} from '@/features/projects/project-details/api/private-notes';
import { useToast } from '@/shared/hooks/use-toast';

export const getPrivateNoteQueryKey = (projectId: number) => [
  'private-notes',
  projectId,
];

export const usePrivateNotes = (projectId: number) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const { data: profileId } = useProfileId();

  const query = useQuery({
    queryKey: getPrivateNoteQueryKey(projectId),
    queryFn: () => getMyPrivateNote({ projectId }),
  });

  const mutation = useMutation({
    mutationFn: ({ content }: { content: string }) => {
      if (!profileId) {
        throw new Error('User not authenticated');
      }
      return upsertMyPrivateNote({ projectId, content, profileId: profileId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getPrivateNoteQueryKey(projectId),
      });
      toast({ title: t('private_note_saved') });
    },
    onError: () => {
      toast({
        title: t('private_note_error'),
        variant: 'destructive',
      });
    },
  });

  return {
    data: query.data ?? '',
    isLoading: query.isLoading,
    mutation,
  };
};
